import { api } from '@goaltree/backend/convex/_generated/api'
import { cn } from '@goaltree/ui/lib/utils'
import { useNavigate } from '@tanstack/react-router'
import { useMutation, useQuery } from 'convex/react'
import { CheckIcon, CopyIcon, MailIcon, PlusIcon, RefreshCwIcon, XIcon } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

const STATUS_TONE = {
  pending: 'text-muted-foreground',
  processed: 'text-primary',
  failed: 'text-destructive',
  ignored: 'text-muted-foreground',
} as const

export function EmailToGoaltreeSettings() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const me = useQuery(api.inboundEmail.myInboundAddress)
  const emails = useQuery(api.inboundEmail.recentInboundEmails)
  const ensure = useMutation(api.inboundEmail.ensureInboundAddress)
  const regenerate = useMutation(api.inboundEmail.regenerateInboundAddress)
  const addSender = useMutation(api.inboundEmail.addAllowedSender)
  const removeSender = useMutation(api.inboundEmail.removeAllowedSender)
  const makeTask = useMutation(api.inboundEmail.taskFromEmail)
  const [sender, setSender] = useState('')
  const ensured = useRef(false)

  useEffect(() => {
    if (me !== null || ensured.current) return
    ensured.current = true
    void ensure({})
  }, [me, ensure])

  if (!me) {
    return (
      <div className="px-4 py-3.5">
        <div className="h-4 w-56 max-w-full animate-pulse rounded bg-muted" />
        <div className="mt-1.5 h-3 w-72 max-w-full animate-pulse rounded bg-muted/60" />
      </div>
    )
  }

  const open = (url: string) => {
    const u = new URL(url, window.location.origin)
    navigate({
      to: u.pathname,
      search: Object.fromEntries(u.searchParams.entries()),
    } as unknown as Parameters<typeof navigate>[0])
  }

  async function submitSender(e: React.FormEvent) {
    e.preventDefault()
    if (!sender.trim()) return
    try {
      await addSender({ email: sender })
      setSender('')
    } catch {
      toast.error(t('inboundEmail.senderError'))
    }
  }

  return (
    <div className="divide-y divide-border">
      <div className="flex items-start gap-4 px-4 py-3.5">
        <span className="hidden h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground sm:flex">
          <MailIcon className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <code className="min-w-0 truncate rounded bg-muted px-2 py-1 font-mono text-xs">
              {me.address}
            </code>
            <button
              type="button"
              onClick={() => {
                void navigator.clipboard.writeText(me.address)
                toast.success(t('inboundEmail.copied'))
              }}
              className="flex shrink-0 cursor-pointer items-center gap-1 rounded-lg border px-2.5 py-1 text-xs font-medium transition-colors hover:bg-muted"
            >
              <CopyIcon className="h-3 w-3" />
              {t('inboundEmail.copy')}
            </button>
          </div>
          <p className="mt-1.5 text-xs text-muted-foreground">
            {t('inboundEmail.explainer', {
              emails: [me.accountEmail, ...me.allowedSenders].filter(Boolean).join(', '),
            })}
          </p>
        </div>
      </div>

      <div className="px-4 py-3.5">
        <p className="text-sm font-medium">{t('inboundEmail.senders')}</p>
        <p className="mt-0.5 text-xs text-muted-foreground">{t('inboundEmail.sendersHint')}</p>
        {me.allowedSenders.length > 0 && (
          <ul className="mt-2 flex flex-wrap gap-1.5">
            {me.allowedSenders.map((s) => (
              <li
                key={s}
                className="flex items-center gap-1 rounded-full bg-muted py-0.5 pl-2.5 pr-1 text-xs"
              >
                {s}
                <button
                  type="button"
                  onClick={() => void removeSender({ email: s })}
                  className="cursor-pointer rounded-full p-0.5 text-muted-foreground hover:text-foreground"
                  title={t('inboundEmail.removeSender')}
                >
                  <XIcon className="h-3 w-3" />
                </button>
              </li>
            ))}
          </ul>
        )}
        {me.allowedSenders.length < 3 && (
          <form onSubmit={(e) => void submitSender(e)} className="mt-2 flex gap-2">
            <input
              type="email"
              value={sender}
              onChange={(e) => setSender(e.target.value)}
              placeholder={t('inboundEmail.senderPlaceholder')}
              className="h-8 min-w-0 flex-1 rounded-lg border bg-background px-2.5 text-xs outline-none focus:ring-2 focus:ring-ring"
            />
            <button
              type="submit"
              disabled={!sender.trim()}
              className="flex shrink-0 cursor-pointer items-center gap-1 rounded-lg bg-primary px-3 text-xs font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
            >
              <PlusIcon className="h-3 w-3" strokeWidth={2.5} />
              {t('inboundEmail.addSender')}
            </button>
          </form>
        )}
      </div>

      <div className="px-4 py-3.5">
        <p className="text-sm font-medium">{t('inboundEmail.recent')}</p>
        {!emails?.length ? (
          <p className="mt-1 text-xs text-muted-foreground">{t('inboundEmail.empty')}</p>
        ) : (
          <ul className="mt-2 space-y-2.5">
            {emails.map((e) => (
              <li key={e._id} className="text-xs">
                <div className="flex items-baseline gap-2">
                  <span className="min-w-0 flex-1 truncate font-medium">
                    {e.subject || t('inboundEmail.noSubject')}
                  </span>
                  <span className={cn('shrink-0', STATUS_TONE[e.status])}>
                    {t(`inboundEmail.status.${e.status}`)}
                  </span>
                </div>
                <p className="truncate text-muted-foreground">
                  {e.from} · {new Date(e.receivedAt).toLocaleDateString()}
                </p>
                {e.tasks.length > 0 && (
                  <ul className="mt-1 space-y-0.5">
                    {e.tasks.map((task) => (
                      <li key={task.id}>
                        <button
                          type="button"
                          onClick={() => open(task.url)}
                          className="flex cursor-pointer items-center gap-1.5 text-left text-foreground/80 hover:text-foreground hover:underline"
                        >
                          <CheckIcon
                            className={cn(
                              'h-3 w-3 shrink-0',
                              task.done ? 'text-primary' : 'text-muted-foreground/40',
                            )}
                          />
                          {task.title}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                {(e.status === 'ignored' || e.status === 'failed') && (
                  <button
                    type="button"
                    onClick={() =>
                      void makeTask({ emailId: e._id }).then(() =>
                        toast.success(t('inboundEmail.taskMade')),
                      )
                    }
                    className="mt-1 cursor-pointer rounded-lg border px-2.5 py-1 font-medium transition-colors hover:bg-muted"
                  >
                    {t('inboundEmail.makeTask')}
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <button
        type="button"
        onClick={() => {
          if (!window.confirm(t('inboundEmail.regenerateConfirm'))) return
          void regenerate({}).then(() => toast.success(t('inboundEmail.regenerated')))
        }}
        className="flex w-full cursor-pointer items-center gap-2 px-4 py-3 text-left text-xs font-medium text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground"
      >
        <RefreshCwIcon className="h-3.5 w-3.5" />
        {t('inboundEmail.regenerate')}
      </button>
    </div>
  )
}
